import { FieldValues } from "react-hook-form";

import { Step } from "../Step";
import { StepperProps } from "../types";
import { StepT } from "../Step/types";

import { SidebarHeader } from "./SidebarHeader";
import { LoadingOverlay } from "./LoadingOverlay";
import { GroupSection } from "./GroupSection";

interface SidebarLayoutProps<T extends FieldValues>
  extends Omit<StepperProps<T>, "onNext" | "onBack"> {
  footer?: React.ReactNode;
  title?: string;
  description?: string;
  subTitle?: string;
  groupedSteps: [string, (StepT & { originalIndex: number })[]][];
  onNext: () => void;
  onBack: () => void;
  overlayContent?: React.ReactNode;
}

export const SidebarLayout = <T extends FieldValues>({
  activeStep,
  steps,
  groupedSteps,
  onSkip,
  onNext,
  onBack,
  isLoading = false,
  isNextButtonLoading = false,
  onStepOptionSelect,
  onItemHover,
  size = "desktop",
  isPreview,
  onExit,
  isBackButtonVisible,
  footer,
  title,
  overlayContent,
  description,
  subTitle,
}: SidebarLayoutProps<T>) => {
  if (!steps || !groupedSteps) {
    return null;
  }

  const currentStep = steps[activeStep];
  const sidebarTitle = currentStep.sidebarTitle || title;
  const sidebarSubTitle = currentStep.sidebarSubTitle || subTitle;
  const sidebarDescription = currentStep.sidebarDescription || description;

  return (
    <div className="relative flex h-full w-full overflow-y-hidden">
      <div className="mr-4 flex h-full w-full max-w-[396px] flex-col border-r border-gray-200 bg-white pr-4">
        <SidebarHeader
          title={sidebarTitle}
          subTitle={sidebarSubTitle}
          description={sidebarDescription}
        />
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
          {groupedSteps.map((group, index) => (
            <GroupSection
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              group={group[0]}
              groupIndex={index}
              stepsInGroup={group[1]}
              activeStep={activeStep}
            />
          ))}
        </div>
        {footer && <div className="border-t border-gray-200 bg-white p-2">{footer}</div>}
      </div>
      <div className="flex h-full flex-1 flex-col">
        <div className="min-h-0 flex-1 pb-3">
          <div className="mx-auto h-full max-w-2xl">
            <Step
              layout="sidebar"
              size={size}
              isPreview={isPreview}
              onItemHover={onItemHover}
              onExit={onExit}
              stepNumber={activeStep + 1}
              step={currentStep}
              totalSteps={steps.filter(item => !item.isOptional).length}
              onStepOptionSelect={onStepOptionSelect}
              isBackButtonVisible={isBackButtonVisible}
              onBack={isBackButtonVisible ? onBack : undefined}
              onNext={onNext}
              onSkip={onSkip}
              isNextButtonLoading={isNextButtonLoading}
              overlayContent={overlayContent}
            />
          </div>
        </div>
      </div>

      {isLoading && <LoadingOverlay />}
    </div>
  );
};
